/**
 * AWS Amplify/Cognito error names for MFA operations
 * These are the actual error names thrown by AWS Amplify Auth methods
 */
export enum AmplifyMfaErrorName {
  // Session/Authentication errors
  NOT_AUTHORIZED = 'NotAuthorizedException',
  USER_NOT_CONFIRMED = 'UserNotConfirmedException',
  USER_NOT_FOUND = 'UserNotFoundException',

  // OTP validation errors
  CODE_MISMATCH = 'CodeMismatchException',
  EXPIRED_CODE = 'ExpiredCodeException',
}

/**
 * MFA error types that determine how the error should be handled
 */
export type MfaErrorType = 'session' | 'otp' | 'generic';

/**
 * Result of MFA error type determination
 */
export interface MfaErrorResult {
  /** The type of the error */
  errorType: MfaErrorType;
  /** The original error object */
  error: Error;
}

/**
 * Determines the type of AWS Amplify MFA error for appropriate handling
 *
 * This utility categorizes MFA-related errors from AWS Amplify/Cognito into three types:
 * - 'session': Authentication session has expired or is invalid (user needs to login again)
 * - 'otp': One-time password validation failed (user can retry with correct code)
 * - 'generic': Unexpected or unhandled errors (fallback error handling)
 *
 * @param error - The error object thrown by AWS Amplify Auth methods
 * @returns Object containing the error type and the original error for further handling
 */
export const getMfaErrorType = (error: unknown): MfaErrorResult => {
  const amplifyError = error as Error;

  // Session expiration errors - redirect user to login page
  const sessionErrors = [
    AmplifyMfaErrorName.NOT_AUTHORIZED, // Invalid or expired session
    AmplifyMfaErrorName.USER_NOT_CONFIRMED, // User account not confirmed
    AmplifyMfaErrorName.USER_NOT_FOUND, // User doesn't exist
  ];

  if (sessionErrors.includes(amplifyError.name as AmplifyMfaErrorName)) {
    return { errorType: 'session', error: amplifyError };
  }

  // OTP validation errors - allow user to retry with correct code
  const otpErrors = [
    AmplifyMfaErrorName.CODE_MISMATCH, // Wrong OTP code entered
    AmplifyMfaErrorName.EXPIRED_CODE, // OTP code has expired
  ];

  if (otpErrors.includes(amplifyError.name as AmplifyMfaErrorName)) {
    return { errorType: 'otp', error: amplifyError };
  }

  // All other errors - generic fallback handling with cleanup
  return { errorType: 'generic', error: amplifyError };
};
